import type { View } from '../router/router';
import { router } from '../router/router';
import { apiClient } from '../services/api-client';
import { authService } from '../services/auth.service';
import { getTyrisIcon } from '../components/ui/tyris-icon';
import {
  renderWorkoutSessionCard,
  type WorkoutSession,
} from '../utils/workout-formatter';
import {
  renderAccessDenied,
  renderEmptyState,
  renderErrorState,
  renderLoader,
  renderPageHeader,
} from '../utils/ui';

interface AdminHistoryStudent {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface AdminHistoryItem {
  id: string;
  date: string;
  startedAt: string | null;
  finishedAt: string | null;
  durationSeconds: number | null;
  notes: string | null;
  createdAt: string;
  studentId: string;
  student: AdminHistoryStudent;
  routine: { id: string; name: string } | null;
  setsCount: number;
  exercisesCount: number;
}

interface AdminHistoryMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface AdminHistoryResponse {
  data: AdminHistoryItem[];
  meta: AdminHistoryMeta;
}

interface StudentOption {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  isActive?: boolean;
}

interface WorkoutSessionDetail extends WorkoutSession {
  student: AdminHistoryStudent;
  routine: { id: string; name: string } | null;
  startedAt: string | null;
  finishedAt: string | null;
  durationSeconds: number | null;
  createdAt: string;
}

const PAGE_SIZE = 10;
const LIST_PATH = '/admin/workout-history';

export class AdminGlobalWorkoutHistoryPage implements View {
  private params: Record<string, string> = {};
  private students: StudentOption[] = [];

  async render(): Promise<string> {
    if (!authService.hasRole(['ADMIN'])) {
      return `<div class="card-glass">${renderAccessDenied()}</div>`;
    }

    this.params = router.getParams();

    if (this.params.id) {
      return this.renderDetailShell();
    }

    await this.loadStudents();
    return this.renderListShell();
  }

  afterRender(): void {
    if (!authService.hasRole(['ADMIN'])) return;

    if (this.params.id) {
      void this.loadDetail(this.params.id);
      return;
    }

    this.bindFilters();
    void this.loadList();
  }

  private async loadStudents(): Promise<void> {
    try {
      this.students = await apiClient.get<StudentOption[]>(
        '/api/users/students?includeInactive=true',
      );
    } catch {
      this.students = [];
    }
  }

  private renderListShell(): string {
    const search = this.params.search ?? '';
    const studentId = this.params.studentId ?? '';
    const dateFrom = this.params.dateFrom ?? '';
    const dateTo = this.params.dateTo ?? '';
    const hasFilters = Boolean(search || studentId || dateFrom || dateTo);

    const studentOptions = this.students
      .slice()
      .sort((a, b) =>
        `${a.lastName} ${a.firstName}`.localeCompare(
          `${b.lastName} ${b.firstName}`,
          'es',
        ),
      )
      .map((student) => {
        const name = this.studentName(student);
        const inactive = student.isActive === false ? ' (baja)' : '';
        return `<option value="${this.escapeHtml(student.id)}" ${
          student.id === studentId ? 'selected' : ''
        }>${this.escapeHtml(name)}${inactive} · ${this.escapeHtml(student.email)}</option>`;
      })
      .join('');

    return `
      ${renderPageHeader({
        title: 'Historial Global de Entrenamientos',
        subtitle:
          'Consultá los entrenamientos registrados por todos los estudiantes, con el detalle completo de sus series.',
      })}

      <form id="admin-history-filters" class="card-glass admin-filters-bar" data-testid="admin-global-workout-filters">
        <div class="admin-filter-col admin-filter-col--grow">
          <label class="admin-filter-label" for="history-search">Buscar estudiante</label>
          <input
            id="history-search"
            name="search"
            type="search"
            class="admin-input"
            placeholder="Nombre o correo..."
            value="${this.escapeHtml(search)}"
            maxlength="100"
            autocomplete="off"
            data-testid="admin-global-workout-search"
          />
        </div>

        <div class="admin-filter-col admin-filter-col--grow">
          <label class="admin-filter-label" for="history-student">Estudiante</label>
          <select id="history-student" name="studentId" class="admin-select" data-testid="admin-global-workout-student-filter">
            <option value="">Todos los estudiantes</option>
            ${studentOptions}
          </select>
        </div>

        <div class="admin-filter-col admin-filter-col--fit">
          <label class="admin-filter-label" for="history-from">Desde</label>
          <input id="history-from" name="dateFrom" type="date" class="admin-input" value="${this.escapeHtml(dateFrom)}" data-testid="admin-global-workout-date-from" />
        </div>

        <div class="admin-filter-col admin-filter-col--fit">
          <label class="admin-filter-label" for="history-to">Hasta</label>
          <input id="history-to" name="dateTo" type="date" class="admin-input" value="${this.escapeHtml(dateTo)}" data-testid="admin-global-workout-date-to" />
        </div>

        <div class="admin-filter-col admin-filter-col--auto">
          <button type="submit" class="btn btn-primary btn-sm" data-testid="admin-global-workout-apply-filters">
            ${getTyrisIcon('search', { size: 15 })} Aplicar filtros
          </button>
        </div>

        <div class="admin-filter-col admin-filter-col--auto">
          <a
            href="#${LIST_PATH}"
            class="btn btn-outline btn-sm"
            style="${hasFilters ? '' : 'visibility:hidden;'}"
            data-testid="admin-global-workout-clear-filters"
          >
            ${getTyrisIcon('close', { size: 14 })} Limpiar
          </a>
        </div>
      </form>

      <div id="admin-history-content" data-testid="admin-global-workout-content">
        ${renderLoader('Cargando entrenamientos...')}
      </div>
    `;
  }

  private renderDetailShell(): string {
    const backPath = this.safeBackPath();
    return `
      ${renderPageHeader({
        title: 'Detalle del entrenamiento',
        subtitle: 'Información completa de la sesión y sus series registradas.',
        breadcrumb: [
          {
            label: 'Historial Global de Entrenamientos',
            href: `#${backPath}`,
          },
          { label: 'Detalle' },
        ],
      })}
      <div id="admin-history-content" data-testid="admin-global-workout-detail">
        ${renderLoader('Cargando sesión de entrenamiento...')}
      </div>
    `;
  }

  private bindFilters(): void {
    const form = document.getElementById(
      'admin-history-filters',
    ) as HTMLFormElement | null;
    if (!form) return;

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const formData = new FormData(form);
      const values: Record<string, string> = {};

      for (const key of ['search', 'studentId', 'dateFrom', 'dateTo']) {
        const value = String(formData.get(key) ?? '').trim();
        if (value) values[key] = value;
      }

      const query = new URLSearchParams(values).toString();
      window.location.hash = `#${LIST_PATH}${query ? `?${query}` : ''}`;
    });
  }

  private async loadList(): Promise<void> {
    const content = document.getElementById('admin-history-content');
    if (!content) return;

    content.innerHTML = renderLoader('Cargando entrenamientos...');

    try {
      const response = await apiClient.get<AdminHistoryResponse>(
        '/api/workout-sessions/admin/history',
        { params: this.buildApiParams() },
      );
      content.innerHTML = this.renderHistory(response);
    } catch (error) {
      content.innerHTML = renderErrorState(
        this.errorMessage(error, 'No pudimos cargar el historial de entrenamientos.'),
        'admin-history-retry',
      );
      document
        .getElementById('admin-history-retry')
        ?.addEventListener('click', () => void this.loadList());
    }
  }

  private renderHistory(response: AdminHistoryResponse): string {
    const { data, meta } = response;

    if (data.length === 0) {
      return renderEmptyState({
        icon: getTyrisIcon('en-curso', { size: 48 }),
        title: 'Sin entrenamientos registrados',
        description:
          'No encontramos sesiones que coincidan con los filtros aplicados.',
        actionLabel: 'Limpiar filtros',
        actionHref: `#${LIST_PATH}`,
      });
    }

    const from = (meta.page - 1) * meta.limit + 1;
    const to = Math.min(meta.page * meta.limit, meta.total);

    return `
      <div class="admin-results-bar">
        <span class="badge badge-admin" data-testid="admin-global-workout-count">
          Mostrando ${from}–${to} de ${meta.total} entrenamiento${meta.total !== 1 ? 's' : ''}
        </span>
      </div>

      <div class="global-workout-list">
        ${data.map((item) => this.renderHistoryRow(item)).join('')}
      </div>

      ${this.renderPagination(meta)}
    `;
  }

  private renderHistoryRow(item: AdminHistoryItem): string {
    const name = this.studentName(item.student);
    const date = this.formatLongDate(item.date);
    const timeRange = this.formatTimeRange(item);
    const duration = this.formatDuration(item);
    const statusBadge = item.finishedAt
      ? `<span class="badge badge-student">Finalizada</span>`
      : `<span class="badge badge-honor">Sin finalizar</span>`;
    const detailHash = this.buildDetailHash(item.id);

    return `
      <article class="card-glass card-management global-workout-card" data-testid="admin-global-workout-row-${item.id}">
        <div class="global-workout-row">
          <div class="global-workout-student">
            <a
              href="#/admin/student?id=${encodeURIComponent(item.student.id)}"
              class="global-workout-student-link"
              data-testid="admin-global-workout-student-link-${item.id}"
            >${this.escapeHtml(name)}</a>
            <span class="global-workout-email">${this.escapeHtml(item.student.email)}</span>
          </div>

          <div class="global-workout-date">
            <span class="global-workout-date-main">${this.escapeHtml(date)}</span>
            <span class="global-workout-date-sub">${this.escapeHtml(timeRange)}</span>
          </div>

          <div class="global-workout-meta">
            <span class="global-workout-chip">${item.exercisesCount} ejercicio${item.exercisesCount !== 1 ? 's' : ''}</span>
            <span class="global-workout-chip">${item.setsCount} serie${item.setsCount !== 1 ? 's' : ''}</span>
            ${duration ? `<span class="global-workout-chip">${this.escapeHtml(duration)}</span>` : ''}
            ${statusBadge}
          </div>

          <div class="global-workout-actions">
            <a
              href="${detailHash}"
              class="btn btn-outline btn-sm"
              data-testid="admin-global-workout-detail-link-${item.id}"
            >
              Ver detalle →
            </a>
          </div>
        </div>
        ${item.notes ? `<p class="global-workout-notes">${this.escapeHtml(item.notes)}</p>` : ''}
      </article>
    `;
  }

  private renderPagination(meta: AdminHistoryMeta): string {
    if (meta.totalPages <= 1) return '';

    const current = meta.page;
    const total = meta.totalPages;
    const prev =
      current > 1
        ? `<a href="#${this.buildListHash({ page: current - 1 })}" class="btn btn-outline btn-sm" data-testid="admin-global-workout-prev">← Anterior</a>`
        : `<button type="button" class="btn btn-outline btn-sm" disabled>← Anterior</button>`;
    const next =
      current < total
        ? `<a href="#${this.buildListHash({ page: current + 1 })}" class="btn btn-outline btn-sm" data-testid="admin-global-workout-next">Siguiente →</a>`
        : `<button type="button" class="btn btn-outline btn-sm" disabled>Siguiente →</button>`;

    return `
      <div class="global-workout-pagination">
        ${prev}
        <span>Página <strong>${current}</strong> de <strong>${total}</strong></span>
        ${next}
      </div>
    `;
  }

  private async loadDetail(id: string): Promise<void> {
    const content = document.getElementById('admin-history-content');
    if (!content) return;

    content.innerHTML = renderLoader('Cargando sesión de entrenamiento...');

    try {
      const session = await apiClient.get<WorkoutSessionDetail>(
        `/api/workout-sessions/${encodeURIComponent(id)}`,
      );
      content.innerHTML = this.renderDetail(session);
    } catch (error) {
      content.innerHTML = renderErrorState(
        this.errorMessage(error, 'No pudimos cargar el detalle de la sesión.'),
        'admin-history-detail-retry',
      );
      document
        .getElementById('admin-history-detail-retry')
        ?.addEventListener('click', () => void this.loadDetail(id));
    }
  }

  private renderDetail(session: WorkoutSessionDetail): string {
    const studentName = this.studentName(session.student);
    const statusBadge = session.finishedAt
      ? `<span class="badge badge-student">Finalizada</span>`
      : `<span class="badge badge-honor">Sin finalizar</span>`;
    const date = this.formatLongDate(session.date);
    const startedAt = this.formatTime(session.startedAt);
    const finishedAt = this.formatTime(session.finishedAt);
    const duration = this.formatDuration(session);
    const exerciseCount = new Set(session.sets.map((set) => set.exercise.id))
      .size;

    const setsContent =
      session.sets.length > 0
        ? renderWorkoutSessionCard(
            {
              id: session.id,
              date: session.date,
              notes: session.notes ?? undefined,
              sets: session.sets,
            },
            { showHeader: false },
          )
        : renderEmptyState({
            icon: getTyrisIcon('en-curso', { size: 40 }),
            title: 'Sin series registradas',
            description: 'Esta sesión no tiene series guardadas.',
          });

    return `
      <section class="card-glass global-workout-detail-summary" data-testid="admin-global-workout-detail-summary">
        <div class="global-workout-detail-head">
          <span class="admin-profile-avatar">${getTyrisIcon('perfil', { size: 22 })}</span>
          <div class="admin-profile-title">
            <h3>
              <a href="#/admin/student?id=${encodeURIComponent(session.student.id)}" class="global-workout-student-link" data-testid="admin-global-workout-detail-student-link">
                ${this.escapeHtml(studentName)}
              </a>
            </h3>
            <p>${this.escapeHtml(session.student.email)}</p>
          </div>
          ${statusBadge}
        </div>

        <dl class="admin-dl">
          <dt>Fecha</dt><dd>${this.escapeHtml(date)}</dd>
          <dt>Inicio</dt><dd>${this.escapeHtml(startedAt)}</dd>
          <dt>Fin</dt><dd>${this.escapeHtml(finishedAt)}</dd>
          <dt>Duración</dt><dd>${this.escapeHtml(duration || '—')}</dd>
          <dt>Ejercicios</dt><dd>${exerciseCount}</dd>
          <dt>Series</dt><dd>${session.sets.length}</dd>
          <dt>Rutina</dt><dd>${this.escapeHtml(session.routine?.name ?? 'Sin rutina asociada')}</dd>
          <dt>Registrada</dt><dd>${this.escapeHtml(this.formatLongDate(session.createdAt))}</dd>
          ${session.notes ? `<dt>Notas</dt><dd class="global-workout-notes-detail">${this.escapeHtml(session.notes)}</dd>` : ''}
        </dl>
      </section>

      <section class="global-workout-detail-sets" data-testid="admin-global-workout-detail-sets">
        <h3 class="admin-section-title">
          ${getTyrisIcon('comenzar-entrenamiento', { size: 18 })} Series registradas (${session.sets.length})
        </h3>
        ${setsContent}
      </section>
    `;
  }

  private buildApiParams(): Record<string, string | number> {
    const params: Record<string, string | number> = { limit: PAGE_SIZE };
    const page = this.parsePage(this.params.page);
    params.page = page;

    for (const key of ['search', 'studentId', 'dateFrom', 'dateTo'] as const) {
      const value = this.params[key];
      if (value) params[key] = value;
    }
    return params;
  }

  private buildListHash(
    overrides: Record<string, string | number> = {},
  ): string {
    const values: Record<string, string | number> = {};
    const page = this.parsePage(this.params.page);
    if (page > 1) values.page = page;

    for (const key of ['search', 'studentId', 'dateFrom', 'dateTo'] as const) {
      const value = this.params[key];
      if (value) values[key] = value;
    }

    for (const [key, value] of Object.entries(overrides)) {
      if (value === '' || value === undefined || value === null) {
        delete values[key];
      } else {
        values[key] = value;
      }
    }

    const query = new URLSearchParams(
      Object.entries(values).map(([key, value]) => [key, String(value)]),
    ).toString();
    return `${LIST_PATH}${query ? `?${query}` : ''}`;
  }

  private buildDetailHash(id: string): string {
    const back = this.buildListHash();
    const query = new URLSearchParams({ id, back }).toString();
    return `#${LIST_PATH}?${query}`;
  }

  private safeBackPath(): string {
    const back = this.params.back;
    if (back && /^\/admin\/workout-history(?:\?|$)/.test(back)) {
      return back;
    }
    return LIST_PATH;
  }

  private parsePage(raw: string | undefined): number {
    const value = Number.parseInt(raw ?? '', 10);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  private studentName(student: AdminHistoryStudent | StudentOption): string {
    const name = `${student.firstName ?? ''} ${student.lastName ?? ''}`.trim();
    return name || 'Estudiante sin nombre';
  }

  private formatLongDate(value: string | null): string {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('es-ES', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }

  private formatTime(value: string | null): string {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  private formatTimeRange(item: AdminHistoryItem): string {
    if (!item.startedAt && !item.finishedAt) return 'Horario no registrado';
    return `${this.formatTime(item.startedAt)} – ${this.formatTime(item.finishedAt)}`;
  }

  private formatDuration(item: {
    durationSeconds: number | null;
    startedAt: string | null;
    finishedAt: string | null;
  }): string {
    let seconds = item.durationSeconds;
    if (seconds == null && item.startedAt && item.finishedAt) {
      const start = new Date(item.startedAt).getTime();
      const end = new Date(item.finishedAt).getTime();
      if (Number.isFinite(start) && Number.isFinite(end) && end >= start) {
        seconds = Math.round((end - start) / 1000);
      }
    }
    if (seconds == null) return '';

    if (seconds < 60) return `${seconds} s`;
    const minutes = Math.round(seconds / 60);
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const remaining = minutes % 60;
    return remaining > 0 ? `${hours} h ${remaining} min` : `${hours} h`;
  }

  private errorMessage(error: unknown, fallback: string): string {
    return error instanceof Error && error.message ? error.message : fallback;
  }

  private escapeHtml(value: string | number | null | undefined): string {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}
